import {
  ReactNode,
  useMemo,
} from 'react'

import {
  InteractionStatus,
} from '@azure/msal-browser'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react'

import { loginRequest } from '@services/msalConfig'
import { useFindProfile } from '@hooks/profile'
import { useMockEnvironment } from '@contexts/mockEnv'
import { useOnlineStatus } from '@hooks/utils'
import { Forbidden } from '@screens/Auth/Forbidden'
import { SignIn } from '@screens/Auth/SignIn'

/**
 * AuthGuard wraps the authenticated app and only renders children
 * when the user profile is loaded (authorized). Shows Forbidden screen
 * if the user has no sites. When offline with cached data, continues
 * to render children.
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const { data, isSuccess, isPaused } = useFindProfile()
  const isOnline = useOnlineStatus()

  const isAuthorized = isAuthenticated && isSuccess && data && data.sites.length > 0

  if (isAuthorized) {
    return <>{children}</>
  }

  // Offline with cached profile data — keep app working
  if (!isOnline && isPaused && data && data.sites.length > 0) {
    return <>{children}</>
  }

  if (isSuccess && data?.sites.length === 0) {
    return <Forbidden />
  }

  // Loading profile...
  return <SignIn forceLoading />
}

/**
 * SessionForm shows the login screen unless MSAL is still starting up,
 * handling a redirect, or the user is already authenticated.
 * Follows the exact same pattern as S2A2-PWA's session-form.tsx.
 */
function SessionForm() {
  const { inProgress, accounts } = useMsal()

  // Already authenticated — don't flash the login screen
  if (accounts && accounts.length > 0) return null

  // Keep the login UI mounted so the Sign In button can show loading.
  if (
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect
  ) return <SignIn />

  return <SignIn />
}

/**
 * Session component gates the app behind MSAL authentication.
 * Follows the exact same pattern as S2A2-PWA's Session component.
 */
export function Session({ children }: { children: ReactNode }) {
  const { accounts, inProgress } = useMsal()
  const { isMockEnvironment } = useMockEnvironment()

  // Mock environment bypasses auth entirely
  if (isMockEnvironment) {
    return <>{children}</>
  }

  const hasAccountInfo = accounts.length > 0

  if (hasAccountInfo) {
    return (
      <>
        <AuthenticatedTemplate>
          <AuthGuard>{children}</AuthGuard>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <p>You need to authenticate to access this App.</p>
        </UnauthenticatedTemplate>
      </>
    )
  } else if (
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Startup
  ) {
    return <SignIn />
  } else {
    return <SessionForm />
  }
}

export function useAuth() {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  const signIn = async () => {
    if (inProgress !== InteractionStatus.None) return
    instance.loginRedirect(loginRequest)
  }

  return useMemo(
    () => ({
      signIn,
      isAuthenticated,
    }),
    [isAuthenticated, inProgress],
  )
}
